import React, {useEffect, useMemo, useState} from "react";
import { Box, Grid, Typography } from "@material-ui/core";
import styled from "styled-components/macro";
import { GenericSidePanel } from "../../../../components/SidePanel/GenericSidePanel.comp";
import MonthRangePicker from "../../../../components/DatePickers/MonthRangePicker.comp";
import { HorizontalBar } from "react-chartjs-2";
import Chart from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {CustomSelect} from "../../../SchoolDashboard/Assessment/components/Select.comp";
import {LEVEL_TO_STATUS, STATUS_COLORS, STATUS_ORDER, STATUS_ORDER_INDEX_TO_COLOR_KEY} from "../../../../utils/constants";
import {statusToDisplay, useBarChartData} from "../hooks/useBarChartData";
import {useLocation} from "react-router-dom";
import {GET_APP_LOCALIZE_TEXT} from "../../../../utils/dq_lc_service/lc_service";
import {useAuth} from "../../../../context/auth";
import {useGlobalState} from "../../../../context/global_state";
import {getModuleIndicatorTranslationsService, getUniqueModuleAssessL6CodesService} from "../../../../services/school.service";
import { getSchoolScoreYears } from "./schoolScoreYears";

if (Chart.plugins.getAll().findIndex(plugin => plugin.id === 'datalabels') === -1) {
    Chart.plugins.register(ChartDataLabels);
}

const getYears = (pageLc) => getSchoolScoreYears(pageLc);

const LegendSquare = styled(Box)`
    width: 60px;
    height: 24px;
    background: ${props => props.color};
    flex-shrink: 0;
`;

const LegendText = styled(Typography)`
    color: #19174a;
    font-weight: 700;
    font-size: 15px;
    @media (min-width: 768px) and (max-width: 1024px) {
        font-size: 9px;
    }
    @media (max-width: 768px) {
        font-size: 8px;
    }
`;

const LegendRow = styled(Box)`
    display: flex;
    align-items: center;
`;

const PanelBox = styled(Box)`
    background: #fff;
    border-radius: 8px;
    padding: 18px 18px 16px 18px;
    box-shadow: 0 2px 8px rgba(10,20,40,0.04);
    min-width: 260px;
    margin-bottom: 12px;
`;

function ScoreByAgeTab({ byAgeData, assessIndicators, selectedCategory, setSelectedCategory, monthRange, setMonthRange, scoresOverviewNationalScores, orgCountry }) {
    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];
    const years = getYears(pageLc);

    const { settings } = useGlobalState();
    const module_code = settings?.selected_report;
    const {authUser} = useAuth();
    const {org_code} = authUser || {};

    const selectedIndicator = useMemo(() => {
        if (!selectedCategory) return undefined;
        return (
            assessIndicators.find(i => i.indicator_code === selectedCategory) ||
            assessIndicators.find(i => i.name === selectedCategory) ||
            // Fallback: allow chart to render even if assessIndicators doesn't contain this code yet
            { indicator_code: selectedCategory }
        );
    }, [assessIndicators, selectedCategory]);

    // Side panel - indicators (by module) + time period selection (matches BreakdownEfficacyTab)
    const timeHeader = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__select_time");
    const competencyHeader = GET_APP_LOCALIZE_TEXT(pageLc, "efficacy__select_competency") || "Select a Competency";
    // Use a stable key for checkbox state: years[0] is always "All Years" from getSchoolScoreYears()
    // Display label can still be localized via labelFormatter.
    const selectAllYears = GET_APP_LOCALIZE_TEXT(pageLc, "side_panel__all_year") || "All Years";
    const allYearsKey = years?.[0] || "All Years";
    const MIN_USERS = 10; // threshold to enable selection

    const [modules, setModules] = useState([]);
    const [timePeriods, setTimePeriods] = useState([]);
    const [minDate, setMinDate] = useState(undefined);
    const [maxDate, setMaxDate] = useState(undefined);
    const [timePeriodsReset, setTimePeriodsReset] = useState(false);
    const [resetKey, setResetKey] = useState(false);
    const [timeCheckboxState, setTimeCheckboxState] = useState({});
    const [sidePanelKey, setSidePanelKey] = useState(0);

    const competencyCheckboxState = useMemo(() => {
        if (!selectedCategory || modules.length === 0) return undefined;
        const allIndicatorCodes = modules.flatMap(m => (m.indicators || []).map(ind => ind.indicator_code));
        return Object.fromEntries(allIndicatorCodes.map(code => [code, code === selectedCategory]));
    }, [modules, selectedCategory]);

    useEffect(() => {
        if (modules.length === 0) return;
        const currentCompetencies = modules.flatMap(m => m.indicators || []).map(ind => ind.indicator_code);
        const isSelectedValid = selectedCategory && currentCompetencies.includes(selectedCategory);

        if (!isSelectedValid) {
            const firstCompetency = modules.flatMap(m => m.indicators || []).find(ind => !ind.disabled)?.indicator_code;
            if (firstCompetency && typeof setSelectedCategory === 'function') {
                setSelectedCategory(firstCompetency);
            }
        }
    }, [modules, selectedCategory, setSelectedCategory]);

    useEffect(() => {
        // Initialize time periods list from existing year options
        const _timePeriods = years.slice(1).map(y => y.toString());
        setTimePeriods(_timePeriods);
    }, [pageLc]);

    useEffect(() => {
        if (timePeriods.length > 0) {
            const [firstYear, lastYear] = [timePeriods[0], timePeriods[timePeriods.length - 1]];
            setMinDate(new Date(`${firstYear}-01-01`));
            setMaxDate(new Date(`${lastYear}-12-31`));
            setTimePeriodsReset(true);

            // Initialize time checkbox state with "All Year" selected
            const newTimeState = Object.fromEntries([allYearsKey, ...timePeriods].map(
                (time) => time === allYearsKey ? [time, true] : [time, false]
            ));
            setTimeCheckboxState(newTimeState);
            setSidePanelKey(prev => prev + 1);
            
            // Set initial month range to "All Years" date range on page load
            if (typeof setMonthRange === "function") {
                const startDate = new Date("2000-01-01").toISOString();
                const endDate = new Date("3000-01-01").toISOString();
                setMonthRange([startDate, endDate]);
            }
        }
    }, [timePeriods, allYearsKey]);

    useEffect(() => {
        if (module_code) {
            uniqueAssessL6Codes().catch(() => {});
        }
    }, [module_code]);

    const indicatorCode = selectedIndicator?.indicator_code;
    const nationalEntry = (scoresOverviewNationalScores || []).find(
        (s) => s?.country === orgCountry && (!indicatorCode || s?.assess_code === indicatorCode)
    );
    const globalEntry = (scoresOverviewNationalScores || []).find(
        (s) => s?.country === "Global" && (!indicatorCode || s?.assess_code === indicatorCode)
    );

    const barChartData = useBarChartData({
        byGroupData: byAgeData, 
        selectedIndicator,
        nationalDist: nationalEntry?.distribution,
        globalDist: globalEntry?.distribution,
        groupLabelFormat: g => `${g} ${GET_APP_LOCALIZE_TEXT(pageLc, "stats__years_old")}`, 
    });

    console.log("Age data is", byAgeData, "national", nationalEntry?.distribution, "global is", globalEntry?.distribution, "and selected indicator is", selectedIndicator);
    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [{
                stacked: true,
                ticks: { min: 0, max: 100, callback: value => value + "%" },
                scaleLabel: { display: true, labelString: "%" }
            }],
            yAxes: [{ stacked: true }]
        },
        legend: { display: false },
        tooltips: { enabled: true, mode: "index", intersect: false },
        plugins: {
            datalabels: {
                display: true,
                color: ctx => {
                    const bg = ctx.dataset.backgroundColor;
                    if (bg === "#ffb300") return "#222";
                    return "#fff";
                },
                font: { weight: 'bold', size: 15 },
                formatter: (value, context) => value > 0 ? value + "%" : "",
            }
        },
        layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    };

    const uniqueAssessL6Codes = async () => {
        if (!module_code || !org_code) return;
        const response = await getUniqueModuleAssessL6CodesService(org_code, module_code);
        if (!response?.data?.modules) return;

        const modulesWithDisabled = (response.data.modules || []).map(module => ({
            ...module,
            indicators: (module.indicators || []).map(ind => ({
                ...ind,
                disabled: typeof ind.unique_users_count === 'number' ? (ind.unique_users_count < MIN_USERS) : false
            }))
        }));

        const allModuleCodes = modulesWithDisabled.map(module => module.module_code);
        const allIndicatorCodes = modulesWithDisabled.flatMap(module => (module.indicators || []).map(ind => ind.indicator_code));

        const translationsResponse = await getModuleIndicatorTranslationsService('en', allModuleCodes, allIndicatorCodes);
        if (translationsResponse?.data) {
            const { modules: moduleTranslationsArray = [], indicators: indicatorTranslationsArray = [] } = translationsResponse.data;
            const moduleTranslationsMap = Object.fromEntries(moduleTranslationsArray.map(m => [m.module_code, m.module_name]));
            const indicatorTranslationsMap = Object.fromEntries(indicatorTranslationsArray.map(i => [i.indicator_code, i.indicator_name]));

            const modulesWithTranslations = modulesWithDisabled.map(module => ({
                ...module,
                module_name: moduleTranslationsMap[module.module_code] || module.module_code,
                indicators: (module.indicators || []).map(ind => ({
                    ...ind,
                    indicator_name: indicatorTranslationsMap[ind.indicator_code] || ind.indicator_code
                }))
            }));
            
            // Sort modules: enabled indicators first, then disabled
            const sortedModules = modulesWithTranslations.sort((a, b) => {
                const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                if (aHasEnabled && !bHasEnabled) return -1;
                if (!aHasEnabled && bHasEnabled) return 1;
                return 0;
            });
            
            setModules(sortedModules);
        } else {
            // Sort modules: enabled indicators first, then disabled
            const sortedModules = modulesWithDisabled.sort((a, b) => {
                const aHasEnabled = (a.indicators || []).some(ind => !ind.disabled);
                const bHasEnabled = (b.indicators || []).some(ind => !ind.disabled);
                if (aHasEnabled && !bHasEnabled) return -1;
                if (!aHasEnabled && bHasEnabled) return 1;
                return 0;
            });
            
            setModules(sortedModules);
        }
    };

    const selectAllMonthsOfYears = (years) => {
        if (!years || years.length === 0) return;
        const [startYear, endYear] = [years[0], years[years.length-1]];
        const [startDate, endDate] = [new Date(`${startYear}-01-01`).toISOString(), new Date(`${endYear}-12-31`).toISOString()];
        if (typeof setMonthRange === "function") {
            setMonthRange([startDate, endDate]);
        }
    };

    function onSelectTimePeriod(item, isChecked, state, setState) {
        // If trying to uncheck the currently selected item, re-check it and re-trigger API
        if (!isChecked && state[item]) {
            if (typeof setMonthRange === "function") {
                setMonthRange([null, null]);
                setTimeout(() => {
                    if (item === allYearsKey) {
                        const startDate = new Date("2000-01-01").toISOString();
                        const endDate = new Date("3000-01-01").toISOString();
                        setMonthRange([startDate, endDate]);
                    } else {
                        selectAllMonthsOfYears([item]);
                    }
                }, 0);
            }
            return;
        }
        
        if (!isChecked) return;

        if (item === allYearsKey) {
            // When "All Years" is selected, use special date range
            const startDate = new Date("2000-01-01").toISOString();
            const endDate = new Date("3000-01-01").toISOString();
            if (typeof setMonthRange === "function") {
                setMonthRange([startDate, endDate]);
            }
        } else {
            selectAllMonthsOfYears([item]);
        }

        // IMPORTANT: state keys must match the rendered checkbox titles exactly
        const nextState = Object.fromEntries([allYearsKey, ...timePeriods].map(
            (time) => [time, time === item]
        ));
        setState(nextState);
        // Keep parent state in sync; otherwise GenericSidePanel may re-apply initialCheckboxStates and "undo" the UI toggle
        setTimeCheckboxState(nextState);
    }


    function onClickDatePicker(event, state, setState) {
        const nextState = Object.fromEntries([allYearsKey, ...timePeriods].map(
            (time) => [time, false]
        ));
        setState(nextState);
        setTimeCheckboxState(nextState);
    }

    function onSelectDateRange(dates) {
        const start_date = new Date(Date.parse(dates[0])).toISOString();
        const end_date = new Date(Date.parse(dates[1])).toISOString();
        if (typeof setMonthRange === "function") {
            setMonthRange([start_date, end_date]);
        }
    }

    const flattenedData = modules.flatMap(module => {
        const moduleItem = `MODULE:${module.module_name || module.module_code}`;
        const moduleIndicators = (module.indicators || []).map(ind => ind.indicator_code);
        return [moduleItem, ...moduleIndicators];
    });

    const disabledSecondaryItems = modules.flatMap(module => (module.indicators || []).filter(ind => ind.disabled).map(ind => ind.indicator_code));
    const getIndicatorDisplayName = (indicatorCode) => {
        for (const module of modules) {
            const indicator = (module.indicators || []).find(ind => ind.indicator_code === indicatorCode);
            if (indicator) return indicator.indicator_name || indicatorCode;
        }
        return indicatorCode;
    };

    function onSelectIndicator(item, isChecked, state, setState) {
        const indicatorCode = item;
        const indicatorObj = modules
            .flatMap(m => m.indicators || [])
            .find(ind => ind.indicator_code === indicatorCode);

        if (indicatorObj?.disabled) return;

        if (isChecked) {
            setSelectedCategory(indicatorCode);
            const nextState = Object.fromEntries(
                Object.keys(state || {}).map((k) => [k, k === indicatorCode])
            );
            setState(nextState);
            return;
        }

        if (selectedCategory === indicatorCode) {
            setState({ ...(state || {}), [indicatorCode]: true });
            return;
        }

        setState({ ...(state || {}), [indicatorCode]: false });
    }

    const handleTimePeriodsResetAcknowledged = () => {
        setTimePeriodsReset(false);
    };

    const SidePanel = (
        <PanelBox>
            <GenericSidePanel
                key={sidePanelKey}
                data={[
                    [allYearsKey, ...timePeriods],
                    flattenedData,
                ]}
                headers={[
                    timeHeader,
                    competencyHeader,
                ]}
                visible={[true, true]}
                colourize={[false, false]}
                defaultCheckIndices={[0, -1]}
                singleSelectIndices={[0, undefined]}
                initialCheckboxStates={[
                    timeCheckboxState,
                    competencyCheckboxState,
                ]}
                onCheckCallbacks={[
                    onSelectTimePeriod,
                    onSelectIndicator,
                ]}
                footers={[
                    MonthRangePicker,
                    undefined,
                ]}
                footersData={[
                    {onChangeDate: onSelectDateRange, minDate, maxDate, resetKey},
                    undefined,
                ]}
                footersCallbacks={[
                    {onOpen: onClickDatePicker},
                    undefined,
                ]}
                timePeriodsReset={timePeriodsReset}
                onResetAcknowledged={handleTimePeriodsResetAcknowledged}
                disabledItems={[
                    [],
                    disabledSecondaryItems,
                ]}
                labelFormatters={[
                    (value) => value === allYearsKey ? selectAllYears : value,
                    getIndicatorDisplayName,
                ]}
            />
        </PanelBox>
    );

    return (
        <Box px={2} py={2} width="100%">
            <Grid container spacing={0} alignItems="center" style={{ marginBottom: 16 }}>
                <Grid item xs={12} md={8}>
                    <Box style={{paddingLeft:'30px'}}>
                        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 6 }}>
                            {GET_APP_LOCALIZE_TEXT(pageLc, "school_score__performance_dist_age")}
                        </Typography>
                        <Typography style={{ color: "#444", fontSize: 16,paddingRight:'75px' }}>
                            {GET_APP_LOCALIZE_TEXT(pageLc, "school_score__age_description")}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 6, textAlign:'center' }}>
                            {GET_APP_LOCALIZE_TEXT(pageLc, "school_score__rubric")}
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <LegendText style={{ marginRight: 8 }}>
                                {statusToDisplay(STATUS_ORDER[0])}
                            </LegendText>
                            {STATUS_ORDER.map((key, index) => {
                                const colorKey = !key || key === ""
                                    ? STATUS_ORDER_INDEX_TO_COLOR_KEY[index] 
                                    : key;
                                const color = STATUS_COLORS[colorKey];
                                return (
                                    <LegendSquare
                                        key={index}
                                        color={color}
                                        style={{ margin: 0, borderRadius: 0 }}
                                    />
                                );
                            })}
                            <LegendText style={{ marginLeft: 8 }}>
                                {statusToDisplay(STATUS_ORDER[STATUS_ORDER.length - 1])}
                            </LegendText>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={0} style={{marginTop:'30px'}} alignItems="flex-start">
                {/* Side Panel */}
                <Grid item xs={12} md={4} lg={3}>
                    {SidePanel}
                </Grid>
                {/* Chart Section */}
                <Grid item xs={12} md={8} lg={9}>
                    <Box
                        style={{
                            height: 40, // Adjust as needed for spacing
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 18,
                        }}
                    >
                        {GET_APP_LOCALIZE_TEXT(pageLc, "school_score__percent_students_rubric_age")}
                    </Box>
                    <Box style={{ background: "#f6f6f6", padding: 24, borderRadius: 8, height: 380 }}>
                        <HorizontalBar
                            data={barChartData}
                            options={barChartOptions}
                            height={300}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ScoreByAgeTab;
